/**
 * AliExpress affiliate SKU attribute parsing, normalization, and stable keys.
 * Keep in sync with server/utils/aliexpressSkuAttrs.js (Node extraction path).
 */

/** Title-case each word; collapse whitespace. */
export function normalizeOptionKey(key: string): string {
  const t = String(key ?? '')
    .trim()
    .replace(/\s+/g, ' ')
  if (!t) return ''
  return t
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Parse raw sku_attr strings from AliExpress:
 * - "Color:Black;Size:M"
 * - "color : black ; Size : M"
 * - "|" or newlines as separators
 * Malformed segments are skipped.
 */
export function parseSkuAttrString(raw: string | null | undefined): Record<string, string> {
  if (raw == null) return {}
  const str = String(raw).trim()
  if (!str) return {}
  const out: Record<string, string> = {}
  const segments = str.split(/[;\n|]+/)
  for (const seg of segments) {
    const p = seg.trim()
    if (!p) continue
    const idx = p.indexOf(':')
    if (idx <= 0) continue
    const k = p.slice(0, idx).trim()
    const v = p.slice(idx + 1).trim()
    if (!k || !v) continue
    out[k] = v
  }
  return out
}

/**
 * Normalize option keys/values, dedupe keys case-insensitively (last wins),
 * strip empty entries, trim values.
 */
function isAeInternalLiteralKey(k: string): boolean {
  return /^__ae_[a-z0-9_]+$/i.test(String(k ?? '').trim())
}

export function cleanOptions(raw: Record<string, string> | null | undefined): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {}
  /** canonical lower -> { key, value } last wins */
  const bucket = new Map<string, { key: string; value: string }>()
  const literals: Record<string, string> = {}
  for (const [k0, v0] of Object.entries(raw)) {
    const ks = String(k0 ?? '').trim()
    if (isAeInternalLiteralKey(ks)) {
      const val = String(v0 ?? '')
        .trim()
        .replace(/\s+/g, ' ')
      if (val) literals[ks] = val
      continue
    }
    const nk = normalizeOptionKey(k0)
    if (!nk) continue
    const val = String(v0 ?? '')
      .trim()
      .replace(/\s+/g, ' ')
    if (!val) continue
    bucket.set(nk.toLowerCase(), { key: nk, value: val })
  }
  return Object.fromEntries(
    Object.entries({ ...Object.fromEntries([...bucket.values()].map((x) => [x.key, x.value])), ...literals }).sort(
      ([a], [b]) => a.localeCompare(b),
    ),
  )
}

function fnv1a64Hex(input: string): string {
  let h = 0xcbf29ce484222325n
  const prime = 0x100000001b3n
  for (let i = 0; i < input.length; i++) {
    h ^= BigInt(input.charCodeAt(i))
    h = (h * prime) & 0xffffffffffffffffn
  }
  return h.toString(16).padStart(16, '0')
}

/**
 * Stable deterministic id for a variant's option map (for barcode / dedup).
 * Same options → same hash across imports.
 */
export function variantAttributeHash(options: Record<string, string>): string {
  const c = cleanOptions(options)
  const keys = Object.keys(c).sort((a, b) => a.localeCompare(b))
  const payload = keys.map((k) => `${k}=${c[k]}`).join('\u0001')
  return fnv1a64Hex(payload)
}

/**
 * Human-readable title from options; avoids generic "Variant N" when values exist.
 */
export function buildVariantDisplayTitle(
  options: Record<string, string>,
  skuId: string | null | undefined,
  index: number,
): string {
  const c = cleanOptions(options)
  const vals = Object.values(c).filter(Boolean)
  if (vals.length > 0) return vals.join(' / ')
  const id = skuId != null && String(skuId).trim() ? String(skuId).trim() : ''
  if (id) return `SKU ${id.slice(0, 80)}`
  return `Combination ${index + 1}`
}

/**
 * Coerce supplier unit price; use positive product-level fallback when SKU price missing.
 */
export function normalizeSupplierPrice(raw: unknown, fallback: number): number {
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').replace(/[^\d.,-]/g, '').replace(',', '.'))
  if (Number.isFinite(n) && n > 0) return n
  const f = Number(fallback)
  if (Number.isFinite(f) && f > 0) return f
  return 0
}

export function computeSupplierPriceRange(
  variants: Array<{ supplier_price: number }>,
  productLevelPrice: number,
): { min: number; max: number } {
  const nums = variants.map((v) => v.supplier_price).filter((n) => Number.isFinite(n) && n > 0)
  const p = Number(productLevelPrice)
  if (nums.length === 0) {
    const x = Number.isFinite(p) && p > 0 ? p : 0
    return { min: x, max: x }
  }
  return { min: Math.min(...nums), max: Math.max(...nums) }
}
